import { useState, useRef, useEffect, useCallback } from 'react';
import { useDiscussions } from '@/hooks/useDiscussions';
import { processQuery, type UserRole } from '@/mocks/ai-logic';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatHistoryItem {
  id: string;
  title: string;
  date: string;
  preview: string;
}

const mockHistory: ChatHistoryItem[] = [
  { id: 'chat-1', title: 'Анализ продаж за июнь', date: '2026-06-21', preview: 'Сводка по заказам, топ позиций...' },
  { id: 'chat-2', title: 'Проверка остатков тканей', date: '2026-06-20', preview: 'Остатки х/б ткани, льна, шёлка...' },
  { id: 'chat-3', title: 'Просроченные заказы', date: '2026-06-19', preview: 'Два просроченных заказа найдено...' },
  { id: 'chat-4', title: 'Статистика менеджеров', date: '2026-06-18', preview: 'Иванов лидирует по выручке...' },
  { id: 'chat-5', title: 'Клиент ТекстильПро', date: '2026-06-17', preview: 'История заказов контрагента...' },
];

const QUICK_COMMANDS = [
  { label: '📊 Сводка', query: 'Сводка по заказам' },
  { label: '📅 За 2 дня', query: 'Заказы за последние 2 дня и их маржинальность' },
  { label: '🏆 Топ позиций', query: 'Топ позиций по выручке' },
  { label: '📦 Остатки', query: 'Остатки на складе' },
];

const WELCOME_MESSAGES: Record<UserRole, string> = {
  director:
    "Здравствуйте! Я AI-ассистент 1С:УТ. У Вас полный доступ ко всем данным компании.\n\nЯ могу помочь с:\n📊 Сводками по заказам\n🏆 Топом позиций и клиентов\n👔 Статистикой по менеджерам\n📦 Остатками на складах\n🚨 Просроченными заказами\n\nЗадайте вопрос — и я найду нужную информацию!",
  manager:
    "Здравствуйте! Я AI-ассистент 1С:УТ. Вы работаете в режиме менеджера — я покажу данные только по Вашим заказам и контрагентам.\n\nЯ могу помочь с:\n📊 Сводками по Вашим заказам\n🏆 Топом позиций и клиентов\n📦 Остатками на складах\n🚨 Просроченными заказами\n\nЗадайте вопрос — и я найду нужную информацию!",
};

export default function DiscussionsDrawer() {
  const { isOpen, close, role, setRole, managerName, setManagerName } = useDiscussions();
  const [drawerWidth, setDrawerWidth] = useState(420);
  const [showHistory, setShowHistory] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>(() => [
    {
      id: 'welcome',
      content: WELCOME_MESSAGES[role],
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setMessages([
      {
        id: 'welcome-' + Date.now(),
        content: WELCOME_MESSAGES[role],
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    setActiveChatId(null);
  }, [role]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Disable body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleResize = useCallback((delta: number) => {
    setDrawerWidth(prev => Math.max(320, Math.min(700, prev + delta)));
  }, []);

  const handleNewChat = () => {
    setMessages([
      {
        id: 'welcome-' + Date.now(),
        content: WELCOME_MESSAGES[role],
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    setActiveChatId(null);
    setShowHistory(false);
  };

  const handleSelectChat = (id: string) => {
    setMessages([]);
    setActiveChatId(id);
    setShowHistory(false);
  };

  const handleSend = async (text: string) => {
    const userMsg: Message = {
      id: 'user-' + Date.now(),
      content: text,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsProcessing(true);

    try {
      const response = await processQuery(text, { role, managerName });
      const aiMsg: Message = {
        id: 'ai-' + Date.now(),
        content: response,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch {
      setMessages(prev => [
        ...prev,
        {
          id: 'error-' + Date.now(),
          content: 'Извините, произошла ошибка при обработке запроса. Пожалуйста, попробуйте ещё раз.',
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const trimmed = input.trim();
      if (!trimmed || isProcessing) return;
      handleSend(trimmed);
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex"
      style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/30"
        onClick={close}
      />

      {/* Drawer */}
      <div
        className="relative ml-auto h-full bg-white border-l border-[#D5CA8A] flex flex-col animate-slide-in-right"
        style={{ width: drawerWidth }}
      >
        {/* Resize handle */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#FDF2B5]/60 z-10"
          onMouseDown={(e) => {
            const startX = e.clientX;
            const handleMouseMove = (moveEvent: MouseEvent) => {
              handleResize(startX - moveEvent.clientX);
            };
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        />

        {/* Header */}
        <div className="flex-shrink-0 border-b border-[#D5CA8A] bg-[#FBED9E]">
          {/* Title row */}
          <div className="h-[32px] flex items-center justify-between px-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#FDF2B5] flex items-center justify-center border border-[#D5CA8A]">
                <i className="ri-robot-line text-[9px] text-[#2D2D2D]"></i>
              </div>
              <span className="text-[12px] font-semibold text-[#2D2D2D]">Обсуждения</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded bg-[#FDF2B5] text-[#2D2D2D] border border-[#D5CA8A]`}>
                {role === 'director' ? 'Руководитель' : 'Менеджер'}
              </span>
            </div>
            <button
              onClick={close}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-[#E8DB6C] text-[#666666] cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-[12px]"></i>
            </button>
          </div>

          {/* Role + Manager row */}
          <div className="flex items-center gap-2 px-3 pb-2">
            <div className="flex items-center gap-1 bg-[#FDF2B5] rounded p-[2px]">
              <button
                onClick={() => setRole('manager')}
                className={`flex items-center gap-1 px-2 py-[2px] rounded text-[10px] font-medium cursor-pointer transition-all whitespace-nowrap ${
                  role === 'manager'
                    ? 'bg-[#E8DB6C] text-[#2D2D2D]'
                    : 'text-[#2D2D2D] hover:bg-[#E8DB6C]'
                }`}
              >
                <i className="ri-user-line text-[9px]"></i>
                Менеджер
              </button>
              <button
                onClick={() => setRole('director')}
                className={`flex items-center gap-1 px-2 py-[2px] rounded text-[10px] font-medium cursor-pointer transition-all whitespace-nowrap ${
                  role === 'director'
                    ? 'bg-[#E8DB6C] text-[#2D2D2D]'
                    : 'text-[#2D2D2D] hover:bg-[#E8DB6C]'
                }`}
              >
                <i className="ri-vip-crown-line text-[9px]"></i>
                Руководитель
              </button>
            </div>
            {role === 'manager' && (
              <select
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
                className="text-[10px] bg-[#FDF2B5] border border-[#D5CA8A] rounded px-2 py-[2px] text-[#2D2D2D] focus:outline-none cursor-pointer"
              >
                {['Иванов А.С.', 'Петров Д.Н.', 'Сидорова Е.М.'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* History toggle + New chat */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#D5CA8A] bg-[#FFF9E6] flex-shrink-0">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-[10px] text-[#666666] hover:text-[#2D2D2D] cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className={showHistory ? 'ri-arrow-up-s-line text-[10px]' : 'ri-arrow-down-s-line text-[10px]'}></i>
            История диалогов
          </button>
          <button
            onClick={handleNewChat}
            className="flex items-center gap-1 px-2 py-[2px] text-[10px] text-[#2D2D2D] bg-[#FDF2B5] border border-[#D5CA8A] rounded hover:bg-[#E8DB6C] cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line text-[10px]"></i>
            Новый чат
          </button>
        </div>

        {/* History list */}
        {showHistory && (
          <div className="max-h-[140px] overflow-y-auto border-b border-[#D5CA8A] bg-[#FFF9E6] flex-shrink-0">
            {mockHistory.map(chat => (
              <button
                key={chat.id}
                onClick={() => handleSelectChat(chat.id)}
                className={`w-full text-left px-3 py-1.5 text-[10px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer ${
                  activeChatId === chat.id ? 'bg-[#E8DB6C]' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium truncate">{chat.title}</span>
                  <span className="text-[#888888] shrink-0">{new Date(chat.date).toLocaleDateString('ru-RU')}</span>
                </div>
                <p className="text-[#888888] truncate">{chat.preview}</p>
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 py-3">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'} mb-3`}>
              <div className={`flex max-w-[85%] ${msg.isUser ? 'flex-row-reverse' : 'flex-row'} items-end gap-1.5`}>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border ${
                    msg.isUser
                      ? 'bg-[#FDF2B5] border-[#D5CA8A] text-[#2D2D2D]'
                      : 'bg-[#FBED9E] border-[#D5CA8A] text-[#2D2D2D]'
                  }`}
                >
                  <i className={`text-[10px] ${msg.isUser ? 'ri-user-line' : 'ri-robot-line'}`}></i>
                </div>
                <div
                  className={`rounded-xl px-3 py-2 border ${
                    msg.isUser
                      ? 'bg-[#FDF2B5] text-[#2D2D2D] rounded-br-sm border-[#D5CA8A]'
                      : 'bg-[#FFF9E6] text-[#2D2D2D] rounded-bl-sm border-[#D5CA8A]'
                  }`}
                >
                  <p className="text-[11px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  <span className={`text-[9px] mt-0.5 block ${msg.isUser ? 'text-[#666666]' : 'text-[#888888]'}`}>
                    {msg.timestamp.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {isProcessing && (
            <div className="flex justify-start mb-3">
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-[#FBED9E] border border-[#D5CA8A] flex items-center justify-center flex-shrink-0">
                  <i className="ri-robot-line text-[10px] text-[#666666]"></i>
                </div>
                <div className="bg-[#FFF9E6] rounded-xl rounded-bl-sm px-4 py-2 border border-[#D5CA8A]">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#666666] animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#666666] animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#666666] animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                    <span className="text-[11px] text-[#666666]">Анализирую данные...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-[#D5CA8A] bg-[#FFF9E6] px-3 py-2 flex-shrink-0">
          {/* Quick command pills */}
          <div className="flex items-center gap-1 mb-2 flex-wrap">
            {QUICK_COMMANDS.map(cmd => (
              <button
                key={cmd.label}
                onClick={() => !isProcessing && handleSend(cmd.query)}
                disabled={isProcessing}
                className="whitespace-nowrap px-2 py-[3px] text-[10px] text-[#2D2D2D] bg-[#FFFDF5] border border-[#D5CA8A] rounded-full hover:bg-[#FBED9E] hover:border-[#E8DB6C] disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer leading-none"
              >
                {cmd.label}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Задайте вопрос... Например: «Сводка по заказам»"
                disabled={isProcessing}
                rows={1}
                className="w-full resize-none rounded-lg border border-[#D5CA8A] bg-white px-3 py-2 pr-10 text-[11px] text-[#2D2D2D] placeholder-[#999999] focus:outline-none focus:border-[#FBED9E] focus:ring-1 focus:ring-[#FBED9E]/20 transition-colors disabled:opacity-50"
              />
              {input.length > 0 && (
                <span className="absolute right-2 bottom-2 text-[10px] text-[#999999]">{input.length}</span>
              )}
            </div>
            <button
              onClick={() => {
                const trimmed = input.trim();
                if (!trimmed || isProcessing) return;
                handleSend(trimmed);
                setInput('');
                if (textareaRef.current) {
                  textareaRef.current.style.height = 'auto';
                }
              }}
              disabled={isProcessing || !input.trim()}
              className="whitespace-nowrap flex items-center gap-1 px-3 py-2 bg-[#FDF2B5] text-[#2D2D2D] border border-[#D5CA8A] rounded-lg hover:bg-[#E8DB6C] disabled:opacity-40 disabled:cursor-not-allowed transition-all text-[11px] font-medium cursor-pointer"
            >
              {isProcessing ? (
                <i className="ri-loader-4-line animate-spin text-[11px]"></i>
              ) : (
                <i className="ri-send-plane-fill text-[11px]"></i>
              )}
            </button>
          </div>
          <p className="text-[9px] text-[#888888] mt-1 text-center">
            AI-ассистент работает только на чтение данных 1С. Enter — отправить, Shift+Enter — новая строка.
          </p>
        </div>
      </div>
    </div>
  );
}